import React from 'react';
import { createPortal } from 'react-dom';
import './StorageQuotaPopup.css';

interface StorageQuotaPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  filesCount?: number;
  type?: 'quota_exceeded' | 'insufficient_storage';
  storageData?: {
    needed: string;
    available: string;
    shortfall: string;
    filesCount: number;
  };
}

export function StorageQuotaPopup({ 
  isOpen, 
  onClose, 
  onUpgrade, 
  filesCount = 1, 
  type = 'quota_exceeded',
  storageData 
}: StorageQuotaPopupProps) {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUpgradeClick = () => {
    onUpgrade();
    onClose();
  };

  return createPortal(
    <div className="storage-quota-overlay" onClick={handleBackdropClick}>
      <div className="storage-quota-popup">
        <div className="storage-quota-header">
          <div className="storage-quota-icon">
            <img src="/previous_icon.png" alt="Storage icon" width="48" height="auto" />
          </div>
          <button className="storage-quota-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="storage-quota-content">
          <h2>Storage Limit Reached</h2>
          <p className="storage-quota-message">
            {type === 'insufficient_storage' && storageData ? (
              `Insufficient storage space. You need ${storageData.needed}MB for ${storageData.filesCount} file${storageData.filesCount > 1 ? 's' : ''}, but only have ${storageData.available}MB available (${storageData.shortfall}MB short).`
            ) : (
              filesCount > 1 
                ? `Cannot upload ${filesCount} files. You've reached your storage limit and need more space to continue uploading.`
                : "Cannot upload file. You've reached your storage limit and need more space to continue uploading."
            )}
          </p>
          
          <div className="storage-quota-options">
            <div className="storage-option">
              <h3>Free up space</h3>
              <p>Delete some existing files from your projects to make room for new uploads.</p>
            </div>
            
            <div className="storage-option-divider">OR</div>
            
            <div className="storage-option">
              <h3>Upgrade your plan</h3>
              <p>Get more storage space and additional features with a paid plan.</p>
            </div>
          </div>
        </div>
        
        <div className="storage-quota-actions">
          <button className="storage-quota-manage" onClick={onClose}>
            Manage Files
          </button>
          <button className="storage-quota-upgrade" onClick={handleUpgradeClick}>
            Upgrade Plan
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
} 